(ns practitest.frontend.global-fields-settings.events
  (:require
    [ajax.core :as ajax]
    [re-frame.core :as re-frame]
    [clojure.string :as str]
    [clojure.set :as set]
    [practitest.frontend.common.i18n :refer [get-translation format-translation]]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.common-old-fe.logging :as log]
    [practitest.frontend.components.notifications :as notifications]
    [practitest.frontend.events :as events]
    [practitest.frontend.routing.router :as router]
    [practitest.frontend.global-fields-settings.common :refer [global-field-form-id entities strip-global-field-icon]]
    [practitest.frontend.global-fields-settings.utils :as global-field-utils]
    [practitest.frontend.routing.backend-routes :as broutes]))

(re-frame/reg-fx
 ::dispatch-browser-event
 (fn [event-name]
   (.dispatchEvent js/window (js/CustomEvent. event-name))
   (.setItem js/localStorage event-name (.toString (js/Date.now)))))

;; ===========================================================================
;; interceptors

(def common-interceptors
  [(when ^boolean js/goog.DEBUG re-frame/debug)
   re-frame/trim-v])

(def form-id global-field-form-id)
(def entity-link-table-id :global-field-entity)
(def migration-wizard-form-id :global-fields-migration-wizard)

(defn- initialize-value-mappings
  "Initialize form fields and value-mappings with auto-matched values.
   Maps each project value to its exact match in global field values, or to itself if no exact match exists.
   Uses index-based field names to avoid collisions from sanitization."
  [db form-id all-project-values global-field-values]
  (reduce (fn [db* [idx project-value]]
            (let [field-name (keyword (str "value-mapping-" idx))]
              (-> db*
                  (assoc-in [:form-states2 form-id :fields field-name :value] project-value)
                  (assoc-in [:form-states2 form-id :wizard-data :value-mappings project-value] project-value)
                  (assoc-in [:form-states2 form-id :wizard-data :value-index-map idx] project-value))))
          db
          (map-indexed vector all-project-values)))

(defn format-field-values-to-srting [field-format possible-values]
  (case field-format
    "linkedlist"
    (str/join ","
              (map (fn [[parent-item items]]
                     (str "{[" (name parent-item) "]:" (str/join "," items) "}"))
                   possible-values))
    ("list" "multilist")
    (str/join "," possible-values)
    nil))

(defn find-id-by-name [fields name]
  (some (fn [{:keys [label id]}]
          (when (= label name) id))
        fields))

(defn find-name-by-id [fields field-id]
  (some (fn [{:keys [id label]}]
          (when (= id field-id) label))
        fields))

(defn- prepare-entity-data [form-fields]
  (let [entities-table-data (get-in form-fields [entity-link-table-id :value])]
    (reduce (fn [acc [entity {:keys [linked mandatory]}]]
                    (if-not linked
                      acc
                      (let [model (entity entities)]
                        (cond-> acc
                          true
                          (update :entities_to_update conj model)

                          mandatory
                          (update :mandatories conj model)))))
            {:entities_to_update []
             :mandatories []}
            entities-table-data)))

(defn- restore-entity-data [entity-data]
  (let [entity-keys (set/map-invert entities)
        linked-entities (:entities_to_update entity-data)
        mandatory-set (set (:mandatories entity-data))]
    (reduce
      (fn [acc entity]
        (let [key (get entity-keys entity)]
          (assoc acc key
                 {:linked true
                  :mandatory  (contains? mandatory-set entity)})))
      {}
      linked-entities)))

(re-frame/reg-event-fx
 ::check-field-relationship
 (fn [{db :db} [_ edit?]]
   (let [{:keys [global-field-format global-parent-list global-field-select-projects] :as form-fields} (get-in db [:form-states2 form-id :fields])
         account_id (get-in db [:current-account :id])
         global-field-id (get-in db [:current-route :path-params :global-field-id])
         uri (broutes/get-url "global_fields#validate_linkage" {:account_id account_id})
         projects-selected? (:value global-field-select-projects)
         entity-data (when  projects-selected? (prepare-entity-data form-fields))
         custom-field {:field_format (:value global-field-format)
                       :enforce_entities projects-selected?}
         params {:parent_list_id (find-id-by-name (:global-fields db) (:value global-parent-list))
                 :id global-field-id
                 :custom_field (merge custom-field entity-data)}]
     {:db (-> db
             (update :progress conj :check-field-relationship-for-link)
             (assoc-in [:form-states2 form-id :loading?] true))
      :http-xhrio
      {:method :post
       :uri uri
       :params  params
       :format (ajax/json-request-format)
       :response-format (ajax/json-response-format {:keywords? true})
       :on-success [::on-success-check-field-relationship edit?]
       :on-failure [::on-fail-check-field-relationship]}})))

(re-frame/reg-event-fx
  ::on-success-check-field-relationship
  (fn [{:keys [db]} [_ edit? response]]
    (let [has-warnings? (:has_warnings response)
          validation-result (:validation_result response)
          base-db (-> db
                      (assoc-in [:form-states2 form-id :validation-link-result] (:type validation-result))
                      (update :progress disj :check-field-relationship-for-link))]
      (if has-warnings?
        {:db (assoc-in base-db [:form-states2 form-id :linkage-modal-data] {:show true
                                                                            :title (get-translation "global-fields.link.confirmation.title" "Field Linkage Warning")
                                                                            :message (:message validation-result)})}
        {:db base-db :dispatch [::save-global-field {:edit? edit?}]}))))

(re-frame/reg-event-fx
::on-fail-check-field-relationship
 (fn [{db :db} [_ response]]
   {:db (-> db
            (update :progress disj :check-field-relationship-for-link)
            (assoc-in [:form-states2 form-id :loading?] false))
    :dispatch [::notifications/error2 (get-in response [:response :message])]}))

(re-frame/reg-event-fx
 ::save-global-field
 (fn [{:keys [db]} [_ {:keys [edit?]}]]
   (let [{:keys [global-field-format global-field-name global-field-possible-values global-parent-list global-field-select-projects] :as form-fields} (get-in db [:form-states2 form-id :fields])
         account_id (get-in db [:current-account :id])
         global-field-id (get-in db [:current-route :path-params :global-field-id])
         event-progress (if edit?
                          ::update-global-field-progress
                          ::create-global-field-progress)
         method (if edit? :put :post)
         uri (if edit?
               (broutes/get-url "accounts#global_fields_update" {:account_id account_id :global-field-id global-field-id})
               (broutes/get-url "accounts#global_fields_create" {:account_id account_id}))
         projects-selected? (:value global-field-select-projects)
         entity-data (if  projects-selected?
                       (prepare-entity-data form-fields)
                       {:entities_to_update []
                        :mandatories []})
         custom-field {:name (:value global-field-name)
                       :field_format (:value global-field-format)}
         params {:custom_field_list_values (format-field-values-to-srting (:value global-field-format) (:value global-field-possible-values))
                 :parent_list_id (find-id-by-name (:global-fields db) (:value global-parent-list))
                 :enforce_entities projects-selected?
                 :validation_type (get-in db [:form-states2 form-id :validation-link-result])
                 :custom_field (merge custom-field entity-data)}]
     {:db (-> db
              (update :progress conj event-progress)
              (assoc-in [:form-states2 form-id :loading?] true))
      :http-xhrio
      {:method          method
       :uri             uri
       :params          params
       :format          (ajax/json-request-format)
       :response-format (ajax/json-response-format {:keywords? true})
       :on-success      [::on-successful-save event-progress edit?]
       :on-failure      [::on-save-fail event-progress]}})))

(re-frame/reg-event-db
 ::init-form-values
 (fn [db  [_ edit-global-field]]
   (let [enforce-entities? (boolean (seq (:enforce_entities edit-global-field)))
         entities-data (restore-entity-data (:enforce_entities edit-global-field))
         form-values {:global-field-name {:dirty? false :errors [] :value (strip-global-field-icon (:label edit-global-field))}
                      :global-field-format {:dirty? false :errors [] :value (:type edit-global-field)}
                      :global-parent-list {:dirty? false :errors [] :value (find-name-by-id (:global-fields db) (:parent_list_id edit-global-field))}
                      :global-field-possible-values {:dirty? false :errors [] :value (:values edit-global-field)}
                      :global-field-select-projects {:dirty? false :errors [] :value enforce-entities?}
                      :global-field-entity {:dirty? false :errors [] :value entities-data}}]
     (update-in db [:form-states2 form-id] assoc :fields form-values))))

(re-frame/reg-event-db
 ::reset-form-values
 (fn [db _]
   (update db :form-states2 dissoc form-id)))

(re-frame/reg-event-fx
 ::on-successful-save
 (fn [{db :db} [_ even-progress edit? response]]
   (let [account_id (get-in db [:current-account :id])
         created-field (:field response)
         global-field-id (:id created-field)
         route-name :practitest.frontend.global-fields-settings.main/edit
         path-params {:account-id account_id :global-field-id global-field-id}
         select-projects? (boolean (:enforce_entities created-field))
         enforce_entities (restore-entity-data (:enforce_entities created-field))]
     {:db  (-> db
               (update :progress disj even-progress)
               (update :global-fields conj created-field)
               (assoc-in [:form-states2 form-id :fields :global-field-name]
                         {:dirty? false :value (strip-global-field-icon (:label created-field))})
               (assoc-in [:form-states2 form-id :fields :global-field-format]
                         {:dirty? false :value (:type created-field)})
               (assoc-in [:form-states2 form-id :fields :global-field-possible-values]
                         {:dirty? false :value (:values created-field)})
               (assoc-in [:form-states2 form-id :fields :global-field-entity]
                         {:dirty? false :value enforce_entities})
               (assoc-in [:form-states2 form-id :fields :global-field-select-projects]
                         {:dirty? false :value select-projects?})
               (assoc-in [:form-states2 form-id :loading?] false))
      :dispatch-n (if edit?
                    [[::notifications/success2 "Global field was successfully updated"]]
                    [[::router/replace-state route-name path-params {}]
                     [::notifications/success2 "Global field was successfully created"]])})))

(re-frame/reg-event-fx
 ::on-save-fail
 (fn [{db :db} [_ event response]]
   {:db (-> db (update :progress disj event)
               (assoc-in [:form-states2 form-id :loading?] false))
    :dispatch [::notifications/error2 (get-in response [:response :message])]}))

(re-frame/reg-event-db
 ::clear-form-loading
 (fn [db _]
   (assoc-in db [:form-states2 form-id :loading?] false)))

(re-frame/reg-event-fx
  :global-fields-settings/init-complete
  (fn [{:keys [db]} _]
    {:db (assoc db :init-complete? true)
     :dispatch [::check-migration-status]}))

(re-frame/reg-event-db
  :global-fields-settings/initialize
  common-interceptors
  (fn [db [options]]
    (log/info ":global-fields-settings/initialize" options db (js->clj js/window._pt :keywordize-keys true))
    (merge options db)))

; Delete global field events

(re-frame/reg-event-fx
  ::delete-field
  (fn [{:keys [db]} [_ id]]
    (let [base-url (dom/get-current-path)
          delete-url (str base-url "/" id)]
      {:db (update db :progress conj :delete-global-field-progress)
       :http-xhrio
       {:method :delete
        :uri delete-url
        :format (ajax/json-request-format)
        :response-format (ajax/json-response-format {:keywords? true})
        :on-success [::field-deleted id]
        :on-failure [::field-deleted-error id]}})))

(re-frame/reg-event-fx
  ::force-delete-field
  (fn [{:keys [db]} [_ id]]
    (let [base-url (dom/get-current-path)
          delete-url (str base-url "/" id "?force=true")]
      {:db (-> db
               (update :progress conj :delete-global-field-progress)
               (assoc-in [:form-states2 form-id :modal-data] {:show false}))
       :http-xhrio
       {:method :delete
        :uri delete-url
        :format (ajax/json-request-format)
        :response-format (ajax/json-response-format {:keywords? true})
        :on-success [::field-deleted id]
        :on-failure [::field-deleted-error id]}})))

(re-frame/reg-event-fx
  ::field-deleted
  (fn [{:keys [db]} [_ id]]
    (let [new-global-fields-list (filter #(not= id (:id %)) (:global-fields db))]
      {:dispatch [::notifications/success2 (get-translation "global-fields.notify.deleted" "Global field was successfully deleted")]
       :db (-> db
               (update :progress disj :delete-global-field-progress)
               (assoc :global-fields new-global-fields-list))})))

(re-frame/reg-event-db
 ::field-deleted-error
 (fn [db [_ id {:keys [response]}]]
   (let [linked-projects (:linked_projects response)
         linkedlist-children (:linkedlist_children response)
         blockers (:blockers response)
         modal-data {:show true
                     :id id
                     :title (get-translation "global-fields.error-deleting.title" "Failed to Delete Field")
                     :field_name (:field_name response)
                     :linked_projects linked-projects
                     :linkedlist_children linkedlist-children
                     :blockers blockers
                     :alert-modal? true}]
     (-> db
         (update :progress disj :delete-global-field-progress)
         (assoc-in [:form-states2 form-id :modal-data] modal-data)))))

;; Convert global field events

(re-frame/reg-event-db
  ::show-convert-confirmation
  (fn [db [_ id target-format]]
    (assoc-in db [:form-states2 form-id :modal-data]
              {:show true :convert-confirm? true
               :convert-field-id id :convert-target target-format})))

(re-frame/reg-event-fx
  ::convert-field
  (fn [{:keys [db]} [_ id target-format]]
    (let [account_id  (get-in db [:current-account :id])
          convert-url (broutes/get-url "global_fields#convert" {:account_id account_id :global_field_id id})]
      {:db (-> db
               (update :progress conj :convert-global-field-progress)
               (assoc-in [:form-states2 form-id :modal-data] {:show false}))
       :http-xhrio
       {:method :post
        :uri convert-url
        :params {:target_format target-format}
        :format (ajax/json-request-format)
        :response-format (ajax/json-response-format {:keywords? true})
        :on-success [::field-converted id]
        :on-failure [::field-convert-error id]}})))

(re-frame/reg-event-fx
  ::field-converted
  (fn [{:keys [db]} [_ id {:keys [field]}]]
    {:dispatch-n [[::events/fetch-fields true]
                  [::notifications/success2
                   (get-translation "global-fields.convert.success" "Field was successfully converted")]]
     :db (-> db
             (update :progress disj :convert-global-field-progress)
             (update :global-fields
                     (fn [gfs] (mapv #(if (= id (:id %)) (merge % field) %) (vec gfs))))
             (cond-> (= (str id) (str (get-in db [:current-route :path-params :global-field-id])))
               (-> (assoc-in [:form-states2 form-id :fields :global-field-format :value]
                              (:field_format field))
                   (assoc-in [:form-states2 form-id :fields :global-field-possible-values :value]
                             (:values field)))))}))

(re-frame/reg-event-fx
  ::field-convert-error
  (fn [{:keys [db]} [_ _id {:keys [response]}]]
    (let [{:keys [blocked message usages]} response
          blocked-action (case (:type (first usages))
                           "auto_filter"      "Field Convert Blocked Auto Filter"
                           "dashboard"        "Field Convert Blocked Dashboard"
                           "field_dependency" "Field Convert Blocked Parent"
                           nil)]
      (when blocked-action
        (mixpanel/track "Global Field Convert" {"Action" blocked-action}))
      (if blocked
        {:db (-> db
                 (update :progress disj :convert-global-field-progress)
                 (assoc-in [:form-states2 form-id :modal-data]
                           {:show true :convert-blocked? true
                            :title (get-translation "global-fields.convert.blocked.title"
                                                    "Field Cannot Be Converted")
                            :message message
                            :usages usages}))}
        {:db (update db :progress disj :convert-global-field-progress)
         :dispatch [::notifications/error2 message]}))))

(re-frame/reg-event-fx
 ::check-item-value-usage
 (fn [{db :db} [_ item parent-item]]
   (let [account_id (get-in db [:current-account :id])
         global-field-id (get-in db [:current-route :path-params :global-field-id])
         check-url (broutes/get-url "global_fields#check_value_usage" {:account_id account_id :global_field_id global-field-id})
         params {:value item
                 :parent_list parent-item}]
     {:db (update db :progress conj :check-possible-values-for-deleting)
      :http-xhrio
      {:method :post
       :uri check-url
       :params  params
       :format (ajax/json-request-format)
       :response-format (ajax/json-response-format {:keywords? true})
       :on-success [::on-success-check-item-value-usage item parent-item]
       :on-failure [::on-fail-check-item-value-usage item parent-item]}})))

(re-frame/reg-event-db
 ::delete-item-from-create-form
 (fn [db [_ value parent-item]]
   (let [possible-values (get-in db [:form-states2 form-id :fields :global-field-possible-values :value])
             new-possible-values (if parent-item
                                   (update possible-values parent-item
                                           (fn [items]
                                             (vec (remove #(= % value) items))))
                                   (vec (remove #(= % value) possible-values)))]
         (assoc-in db [:form-states2 form-id :fields :global-field-possible-values :value]
                   new-possible-values))))

(re-frame/reg-event-fx
 ::delete-possible-value
 (fn [{db :db} [_ value parent-item]]
   (let [account_id (get-in db [:current-account :id])
         global-field-id (get-in db [:current-route :path-params :global-field-id])
         url (broutes/get-url "global_fields#delete_list_value" {:account_id account_id :global_field_id global-field-id})
         params {:value value
                 :parent_list (some-> parent-item name)}]
     {:db (update db :progress conj :delete-possible-values)
      :http-xhrio
      {:method :post
       :uri url
       :params  params
       :format (ajax/json-request-format)
       :response-format (ajax/json-response-format {:keywords? true})
       :on-success [::on-success-delete-possible-value value parent-item]
       :on-failure [::on-fail-delete-possible-value]}})))

(re-frame/reg-event-fx
 ::on-success-delete-possible-value
 (fn [{db :db} [_ value parent-item]]
   (let [possible-values (get-in db [:form-states2 form-id :fields :global-field-possible-values :value])
         new-possible-values (if parent-item
                               (update possible-values parent-item
                                       (fn [items]
                                         (vec (remove #(= % value) items))))
                               (vec (remove #(= % value) possible-values)))]
     {:db (-> db
              (update :progress disj :delete-possible-values)
              (assoc-in [:form-states2 form-id :modal-data] {:show false})
              (assoc-in [:form-states2 form-id :fields :global-field-possible-values :value]
                        new-possible-values))
      :dispatch [::notifications/success2
                 (format-translation
                  (get-translation "global-field.delete-possible-value.success" "Successfully deleted value: %{value}")
                   :value value)]})))

(re-frame/reg-event-fx
 ::on-fail-delete-possible-value
 (fn [{db :db} [_ response]]
   {:db (update db :progress disj :delete-possible-values)
    :dispatch [::notifications/error2 (get-in response [:response :message])]}))

; Rename value events

(defn- rename-in-possible-values [possible-values old-value new-value parent-item]
  (if parent-item
    (update possible-values parent-item (fn [items] (mapv #(if (= % old-value) new-value %) items)))
    (mapv #(if (= % old-value) new-value %) possible-values)))

(re-frame/reg-event-fx
 ::open-rename-value-modal
 (fn [{db :db} [_ old-value parent-item]]
   {:db (assoc-in db [:form-states2 form-id :rename-modal]
                  {:show? true
                   :old-value old-value
                   :new-value old-value
                   :parent-list parent-item
                   :count nil
                   :error nil})
    :dispatch [::fetch-rename-count old-value parent-item]}))

(re-frame/reg-event-db
 ::set-rename-new-value
 (fn [db [_ value]]
   (-> db
       (assoc-in [:form-states2 form-id :rename-modal :new-value] value)
       (assoc-in [:form-states2 form-id :rename-modal :error] nil))))

(re-frame/reg-event-fx
 ::close-rename-modal
 (fn [{db :db} _]
   {:db (update-in db [:form-states2 form-id] dissoc :rename-modal)}))

(re-frame/reg-event-fx
 ::fetch-rename-count
 (fn [{db :db} [_ old-value parent-item]]
   (let [account_id (get-in db [:current-account :id])
         global-field-id (get-in db [:current-route :path-params :global-field-id])]
     {:http-xhrio
      {:method :post
       :uri (broutes/get-url "global_fields#rename_value_count"
                             {:account_id account_id :global_field_id global-field-id})
       :params {:value old-value :parent_list (some-> parent-item name)}
       :format (ajax/json-request-format)
       :response-format (ajax/json-response-format {:keywords? true})
       :on-success [::rename-count-success]
       :on-failure [::rename-count-success]}})))

(re-frame/reg-event-db
 ::rename-count-success
 (fn [db [_ response]]
   (assoc-in db [:form-states2 form-id :rename-modal :count] (:entity_count response 0))))

(re-frame/reg-event-fx
 ::submit-rename-value
 (fn [{db :db} _]
   (let [account_id (get-in db [:current-account :id])
         global-field-id (get-in db [:current-route :path-params :global-field-id])
         {:keys [old-value new-value parent-list]} (get-in db [:form-states2 form-id :rename-modal])]
     {:db (-> db
              (update :progress conj ::rename-value-progress)
              (assoc-in [:form-states2 form-id :rename-modal :error] nil))
      :http-xhrio
      {:method :post
       :uri (broutes/get-url "global_fields#rename_list_value"
                             {:account_id account_id :global_field_id global-field-id})
       :params {:value old-value :new_value new-value :parent_list (some-> parent-list name)}
       :format (ajax/json-request-format)
       :response-format (ajax/json-response-format {:keywords? true})
       :on-success [::rename-value-success old-value new-value parent-list]
       :on-failure [::rename-value-fail]}})))

(re-frame/reg-event-fx
 ::rename-value-success
 (fn [{db :db} [_ old-value new-value parent-list response]]
   (let [possible-values (get-in db [:form-states2 form-id :fields :global-field-possible-values :value])
         new-possible-values (rename-in-possible-values possible-values old-value new-value parent-list)
         message (get-in response [:result :message]
                         (format-translation
                          (get-translation "global-field.rename-value.success" "Successfully renamed value to %{value}")
                          :value new-value))]
     {:db (-> db
              (update :progress disj ::rename-value-progress)
              (update-in [:form-states2 form-id] dissoc :rename-modal)
              (assoc-in [:form-states2 form-id :fields :global-field-possible-values :value] new-possible-values))
      :dispatch [::notifications/success2 message]
      ::dispatch-browser-event "pt:global-field-renamed"})))

(re-frame/reg-event-db
 ::rename-value-fail
 (fn [db [_ response]]
   (-> db
       (update :progress disj ::rename-value-progress)
       (assoc-in [:form-states2 form-id :rename-modal :error]
                 (get-in response [:response :message]
                         (get-translation "global-field.rename-value.error" "Failed to rename value"))))))

(re-frame/reg-event-db
  ::clear-dependent-fields
  (fn [db [_ form-id dependent-fields]]
      (reduce
        (fn [db field]
          (assoc-in db [:form-states2 form-id :fields field] {:value nil
                                                              :dirty? false}))
        db
        dependent-fields)))

(re-frame/reg-event-db
 ::global-fields-hide-modal
 (fn [db [_ modal-key]]
   (assoc-in db [:form-states2 form-id modal-key] {:show false})))

(re-frame/reg-event-fx
 ::fetch-project-fields
 (fn [{:keys [db]} [_ field-format global-field-id & [page]]]
   (let [account-id (get-in db [:current-account :id])
         search-query (get-in db [:form-states2 migration-wizard-form-id :wizard-data :search-query] "")
         current-page (or page 1)
         path-params {:account_id account-id}
         query-params (cond-> {}
                        field-format (assoc :field_format field-format)
                        global-field-id (assoc :global_field_id global-field-id)
                        search-query (assoc :search search-query)
                        current-page (assoc :page current-page))]
     {:db (if (= current-page 1)
            (assoc db :migration-wizard-project-fields-loading? true)
            db) ;; Don't show loading spinner for pagination
      :http-xhrio
      {:method :get
       :uri (broutes/get-url "global_fields#project_fields_for_migration" path-params query-params)
       :format (ajax/json-request-format)
       :response-format (ajax/json-response-format {:keywords? true})
       :on-success [::fetch-project-fields-success page]
       :on-failure [::fetch-project-fields-failure]}})))

(re-frame/reg-event-fx
 ::show-migration-wizard
 (fn [{:keys [db]} [_ field-id]]
   {:dispatch [::check-migration-lock-before-wizard field-id]}))

(re-frame/reg-event-fx
 ::check-migration-lock-before-wizard
 (fn [{:keys [db]} [_ field-id]]
   (let [account-id (get-in db [:current-account :id])]
     {:http-xhrio
      {:method :get
       :uri (broutes/get-url "global_fields#check_migration_lock" {:account_id account-id})
       :format (ajax/json-request-format)
       :response-format (ajax/json-response-format {:keywords? true})
       :on-success [::check-migration-lock-before-wizard-success field-id]
       :on-failure [::check-migration-lock-before-wizard-failure field-id]}})))

(re-frame/reg-event-fx
 ::check-migration-lock-before-wizard-success
 (fn [{:keys [db]} [_ field-id response]]
   (let [migration-locked? (:migration_locked response false)]
     (if migration-locked?
       {:dispatch [::notifications/error2
                   (get-translation "global-fields.migration.locked-message"
                                   "A migration is currently in progress for this account. Please wait for the completion email.")]}
       {:db (assoc db :migration-locked? false)
        :dispatch [::open-migration-wizard field-id]}))))

(re-frame/reg-event-fx
 ::check-migration-lock-before-wizard-failure
 (fn [{:keys [db]} [_ field-id]]
   {:db (assoc db :migration-locked? false)
    :dispatch [::open-migration-wizard field-id]}))

;; Migration status banner events

;; Status refresh for the migration banner (a single, account-wide lock);
;; the banner updates on init and on the manual Refresh button.

(re-frame/reg-event-fx
 ::refresh-status
 (fn [_ [_ _status-type]]
   {:dispatch [::check-migration-status]}))

(re-frame/reg-event-fx
 ::check-migration-status
 (fn [{:keys [db]} _]
   (let [account-id (get-in db [:current-account :id])]
     {:db (assoc-in db [:migration-status :refreshing?] true)
      :http-xhrio
      {:method :get
       :uri (broutes/get-url "global_fields#check_migration_lock" {:account_id account-id})
       :format (ajax/json-request-format)
       :response-format (ajax/json-response-format {:keywords? true})
       :on-success [::check-migration-status-success]
       :on-failure [::check-migration-status-failure]}})))

(re-frame/reg-event-db
 ::check-migration-status-success
 (fn [db [_ response]]
   (let [was-locked? (get-in db [:migration-status :locked?])
         previous-field-name (get-in db [:migration-status :field-name])
         now-locked? (:migration_locked response false)]
     (assoc db :migration-status
            (if now-locked?
              {:locked? true
               :field-name (:field_name response)
               :stage (:stage response)
               :percentage (:percentage response)
               :refreshing? false
               :last-refreshed-at (.now js/Date)}
              (if (and was-locked? previous-field-name)
                {:locked? false
                 :completed? true
                 :field-name previous-field-name
                 :stage "completed"
                 :percentage 100
                 :refreshing? false
                 :last-refreshed-at (.now js/Date)}
                {:locked? false :refreshing? false}))))))

(re-frame/reg-event-db
 ::check-migration-status-failure
 (fn [db _]
   (assoc db :migration-status {:locked? false :field-name nil :refreshing? false})))

(re-frame/reg-event-db
 ::dismiss-migration-status
 (fn [db _]
   (-> db
       (dissoc :migration-status)
       (assoc :migration-locked? false))))

(re-frame/reg-event-fx
 ::open-migration-wizard
 (fn [{:keys [db]} [_ field-id]]
   (let [;; Find the field object from global-fields
         global-fields (:global-fields db)
         selected-field (when field-id
                          (first (filter #(= (:id %) field-id) global-fields)))
         ;; Initialize forms3 state for form fields
         form-state {:fields {:selected-global-field {:value selected-field
                                                       :errors []
                                                       :editing? false}}
                     :base-entity {}}
         ;; Keep wizard-specific state separate
         wizard-data {:show-wizard? true
                      :current-step 1
                      :selected-project-fields []
                      :value-mappings {}
                      :show-confirmation-modal? false
                      :value-sort-order :asc
                      :value-filter-text ""
                      :show-value-filter? false
                      :search-query ""
                      :keep-union? false
                      :last-selected-global-field-id nil
                      :title (get-translation "global-fields.migration.title" "Migrate Global Wizard")}]
     (cond-> {:db (-> db
                       (assoc-in [:form-states2 migration-wizard-form-id] form-state)
                       (assoc-in [:form-states2 migration-wizard-form-id :wizard-data] wizard-data)
                       (assoc :migration-wizard-project-fields [])
                       (assoc :migration-wizard-project-fields-loading? false)
                       (assoc :migration-wizard-project-fields-pagination {}))}
       field-id (assoc :dispatch [::fetch-project-fields (:field_format selected-field) field-id])))))

(re-frame/reg-event-db
 ::fetch-project-fields-success
 (fn [db [_ page response]]
   (let [current-page (or page 1)
         new-fields (:project_fields response)
         pagination (:pagination response)
         current-fields (get db :migration-wizard-project-fields [])
         ;; If page 1, replace; otherwise append for infinite scrolling
         merged-fields (if (= current-page 1)
                         new-fields
                         (vec (concat current-fields new-fields)))]
     (-> db
         (assoc :migration-wizard-project-fields merged-fields)
         (assoc :migration-wizard-project-fields-pagination pagination)
         (assoc :migration-wizard-project-fields-loading? false)
         (assoc :migration-wizard-project-fields-fetching-next? false)))))

(re-frame/reg-event-fx
 ::fetch-project-fields-failure
 (fn [{:keys [db]} [_ _]]
   {:db (-> db
            (assoc :migration-wizard-project-fields-loading? false)
            (assoc :migration-wizard-project-fields-fetching-next? false))
    :dispatch [::notifications/error2 "Failed to fetch project fields" {:auto-close 3000}]}))

(re-frame/reg-event-fx
 ::fetch-next-page
 (fn [{:keys [db]} _]
   (let [pagination (get db :migration-wizard-project-fields-pagination {})
         has-more (:has_more pagination false)
         current-page (:page pagination 1)
         is-fetching (:migration-wizard-project-fields-fetching-next? db false)]
     ;; Only fetch if there are more results and not already fetching
     (when (and has-more (not is-fetching))
       (let [selected-field (get-in db [:form-states2 migration-wizard-form-id :fields :selected-global-field :value])
             field-format (when selected-field (:field_format selected-field))
             field-id (when selected-field (:id selected-field))
             next-page (inc current-page)]
         (if (and field-format field-id)
           {:db (assoc db :migration-wizard-project-fields-fetching-next? true)
            :dispatch [::fetch-project-fields field-format field-id next-page]}
           {}))))))

(re-frame/reg-event-db
 ::hide-migration-wizard
 (fn [db _]
   (assoc-in db [:form-states2 migration-wizard-form-id :wizard-data :show-wizard?] false)))

(re-frame/reg-event-db
 ::cancel-migration-wizard
 (fn [db _]
   (update db :form-states2 dissoc migration-wizard-form-id)))

(re-frame/reg-event-db
 ::migration-next-step
 (fn [db _]
   (let [current-step (get-in db [:form-states2 migration-wizard-form-id :wizard-data :current-step])
         ;; When moving from step 1 to step 2, initialize form fields with auto-matched values
         db (if (= current-step 1)
              (let [selected-global-field (get-in db [:form-states2 migration-wizard-form-id :fields :selected-global-field :value])
                    raw-values (or (:values selected-global-field) [])
                    global-field-values (if (map? raw-values)
                                         (vec (distinct (mapcat val raw-values)))
                                         raw-values)
                    selected-project-fields (get-in db [:form-states2 migration-wizard-form-id :wizard-data :selected-project-fields])
                    all-project-values (global-field-utils/extract-field-values selected-project-fields)
                    db (initialize-value-mappings db migration-wizard-form-id all-project-values global-field-values)]
                (cond-> db
                  (= (:field_format selected-global-field) "linkedlist")
                  (assoc-in [:form-states2 migration-wizard-form-id :wizard-data :keep-union?] true)))
              db)]
     (update-in db [:form-states2 migration-wizard-form-id :wizard-data :current-step] inc))))

(re-frame/reg-event-db
 ::migration-previous-step
 (fn [db _]
   (let [current-step (get-in db [:form-states2 migration-wizard-form-id :wizard-data :current-step])
         db (update-in db [:form-states2 migration-wizard-form-id :wizard-data :current-step] dec)]
     ;; When going back, only reset step-specific state, preserve field selections
     (if (= current-step 2)
       (let [form-fields (keys (get-in db [:form-states2 migration-wizard-form-id :fields] {}))
             value-mapping-fields (filter #(str/starts-with? (name %) "value-mapping-") form-fields)]
         (-> db
             (assoc-in [:form-states2 migration-wizard-form-id :wizard-data :value-mappings] {})
             (assoc-in [:form-states2 migration-wizard-form-id :wizard-data :value-index-map] {})
             (update-in [:form-states2 migration-wizard-form-id :fields]
                       (fn [fields]
                         (reduce (fn [f field-name]
                                   (assoc-in f [field-name :value] nil))
                                 fields
                                 value-mapping-fields)))
             (assoc-in [:form-states2 migration-wizard-form-id :wizard-data :search-query] "")
             (assoc-in [:form-states2 migration-wizard-form-id :wizard-data :show-confirmation-modal?] false)))
       db))))

(re-frame/reg-event-fx
 ::migration-select-global-field
 (fn [{:keys [db]} [_ selected-global-field]]
   (when selected-global-field
     {:db (-> db
              (assoc-in [:form-states2 migration-wizard-form-id :wizard-data :selected-project-fields] [])
              (assoc-in [:form-states2 migration-wizard-form-id :wizard-data :search-query] "")
              (assoc-in [:form-states2 migration-wizard-form-id :wizard-data :last-selected-global-field-id] (:id selected-global-field)))
      :dispatch [::fetch-project-fields (:field_format selected-global-field) (:id selected-global-field)]})))

(re-frame/reg-event-db
 ::migration-toggle-project-field
 (fn [db [_ field field-project-name]]
   (let [current-fields (get-in db [:form-states2 migration-wizard-form-id :wizard-data :selected-project-fields] [])
         field-id (:id field)
         project-id (:project_id field)
         already-selected? (some #(= (:id %) field-id) current-fields)]
     (if already-selected?
       (assoc-in db [:form-states2 migration-wizard-form-id :wizard-data :selected-project-fields]
                 (vec (remove #(= (:id %) field-id) current-fields)))
       (let [field-with-project (assoc field :project_name field-project-name)
             fields-without-same-project (vec (remove #(= (:project_id %) project-id) current-fields))]
         (assoc-in db [:form-states2 migration-wizard-form-id :wizard-data :selected-project-fields]
                   (conj fields-without-same-project field-with-project)))))))

(re-frame/reg-event-fx
 ::migration-update-search
 (fn [{:keys [db]} [_ search-query]]
   (let [selected-global-field (get-in db [:form-states2 migration-wizard-form-id :fields :selected-global-field :value])
         field-format (when selected-global-field (:field_format selected-global-field))
         field-id (when selected-global-field (:id selected-global-field))]
     (cond-> {:db (assoc-in db [:form-states2 migration-wizard-form-id :wizard-data :search-query] search-query)}
       (and field-format field-id)
       (assoc :dispatch [::fetch-project-fields field-format field-id])))))

(re-frame/reg-event-db
 ::migration-clear-selected-fields
 (fn [db _]
   (assoc-in db [:form-states2 migration-wizard-form-id :wizard-data :selected-project-fields] [])))

(re-frame/reg-event-db
 ::migration-toggle-value-sort
 (fn [db _]
   (let [current-order (get-in db [:form-states2 migration-wizard-form-id :wizard-data :value-sort-order] :asc)]
     (assoc-in db [:form-states2 migration-wizard-form-id :wizard-data :value-sort-order]
               (if (= current-order :asc) :desc :asc)))))

(re-frame/reg-event-db
 ::migration-toggle-value-filter
 (fn [db _]
   (update-in db [:form-states2 migration-wizard-form-id :wizard-data :show-value-filter?] not)))

(re-frame/reg-event-db
 ::migration-update-value-filter
 (fn [db [_ filter-text]]
   (assoc-in db [:form-states2 migration-wizard-form-id :wizard-data :value-filter-text] filter-text)))

(re-frame/reg-event-db
 ::migration-toggle-union
 (fn [db _]
   (update-in db [:form-states2 migration-wizard-form-id :wizard-data :keep-union?] not)))

(re-frame/reg-event-db
 ::migration-map-value
 (fn [db [_ project-value global-value field-name]]
   (let [extracted-value (cond
                           (map? global-value) (:label global-value)
                           :else global-value)
         ;; Use provided field-name, or fall back to looking up by index
         field-name (or field-name
                       (let [index-map (get-in db [:form-states2 migration-wizard-form-id :wizard-data :value-index-map])
                             idx (some (fn [[k v]] (when (= v project-value) k)) index-map)]
                         (when idx (keyword (str "value-mapping-" idx)))))
         db* (assoc-in db [:form-states2 migration-wizard-form-id :wizard-data :value-mappings project-value]
                       (when (not= extracted-value "") extracted-value))]
      (if field-name
        (assoc-in db* [:form-states2 migration-wizard-form-id :fields field-name :value]
                 (when (not= extracted-value "") extracted-value))
        db*))))

(re-frame/reg-event-fx
 ::fetch-migration-entity-count
 (fn [{:keys [db]} _]
   (let [selected-project-fields (get-in db [:form-states2 migration-wizard-form-id :wizard-data :selected-project-fields] [])
         project-field-ids (mapv :id selected-project-fields)
         account-id (get-in db [:current-account :id])]
     {:db (assoc db :migration-entity-count-loading? true)
      :http-xhrio
      {:method :post
       :uri (broutes/get-url "global_fields#migration_entity_count" {:account_id account-id})
       :params {:project_field_ids project-field-ids}
       :format (ajax/json-request-format)
       :response-format (ajax/json-response-format {:keywords? true})
       :on-success [::fetch-migration-entity-count-success]
       :on-failure [::fetch-migration-entity-count-failure]}})))

(re-frame/reg-event-db
 ::fetch-migration-entity-count-success
 (fn [db [_ response]]
   (-> db
       (assoc :migration-entity-count (:entity_count response 0))
       (assoc :migration-entity-count-loading? false))))

(re-frame/reg-event-fx
 ::fetch-migration-entity-count-failure
 (fn [{:keys [db]} [_ _]]
   {:db (-> db
            (assoc :migration-entity-count 0)
            (assoc :migration-entity-count-loading? false))
    :dispatch [::notifications/error2 "Failed to fetch migration entity count" {:auto-close 3000}]}))

(re-frame/reg-event-fx
 ::show-confirmation-modal
 (fn [{:keys [db]} _]
   {:db (assoc-in db [:form-states2 migration-wizard-form-id :wizard-data :show-confirmation-modal?] true)
    :dispatch [::fetch-migration-entity-count]}))

(re-frame/reg-event-db
 ::hide-confirmation-modal
 (fn [db _]
   (assoc-in db [:form-states2 migration-wizard-form-id :wizard-data :show-confirmation-modal?] false)))

(re-frame/reg-event-fx
 ::migration-save
 (fn [{:keys [db]} _]
   (let [selected-global-field-obj (get-in db [:form-states2 migration-wizard-form-id :fields :selected-global-field :value])
         selected-global-field-id (:id selected-global-field-obj)
         wizard-data (get-in db [:form-states2 migration-wizard-form-id :wizard-data])
         {:keys [selected-project-fields value-mappings keep-union?]} wizard-data
         account-id (get-in db [:current-account :id])
         ;; Prepare the params for the backend - this will trigger a Sidekiq worker
         params {:global_field_id selected-global-field-id
                 :project_field_ids (mapv :id selected-project-fields)
                 :value_mappings value-mappings
                 :keep_union (boolean keep-union?)}]
     {:db (update db :progress conj ::migrate-global-field-progress)
      :http-xhrio
      {:method :post
       :uri (broutes/get-url "global_fields#migrate" {:account_id account-id})
       :params params
       :format (ajax/json-request-format)
       :response-format (ajax/json-response-format {:keywords? true})
       :on-success [::migration-save-success]
       :on-failure [::migration-save-failure]}})))

(re-frame/reg-event-fx
 ::migration-save-success
 (fn [{:keys [db]} _]
   (let [selected-field (get-in db [:form-states2 migration-wizard-form-id :fields :selected-global-field :value])
         field-name (:label selected-field)]
     {:db (-> db
              (update :progress disj ::migrate-global-field-progress)
              (assoc :migration-locked? true)
              (assoc :migration-status {:locked? true :field-name field-name}))
      :dispatch [::cancel-migration-wizard]})))

(re-frame/reg-event-fx
 ::migration-save-failure
 (fn [{:keys [db]} [_ response]]
   (let [error-message (get-in response [:response :message]
                              (get-translation "global-fields.migration.error"
                                             "Failed to start migration. Please try again."))]
     {:db (update db :progress disj ::migrate-global-field-progress)
      :dispatch [::notifications/error2 error-message]})))

(re-frame/reg-event-db
 ::on-success-check-item-value-usage
 (fn [db [_ item parent-item]]
   (let [modal-data {:show true
                     :delete-value-confirm? true
                     :value item
                     :parent-item parent-item
                     :title (get-translation "global-field.delete-possible-value.modal-title" "Confirm Value Deletion")
                      ;;for defining text-message in delete-modal-message
                     :value-used? false}]
     (-> db
         (update :progress disj :check-possible-values-for-deleting)
         (assoc-in [:form-states2 form-id :modal-data] modal-data)))))

(re-frame/reg-event-db
 ::on-fail-check-item-value-usage
 (fn [db [_ item parent-item {:keys [response]}]]
   (let [modal-data {:show true
                     :delete-value-confirm? true
                     :title (get-translation "global-field.delete-possible-value.modal-title" "Confirm Value Deletion")
                     :value item
                     :parent-item parent-item
                     :projects (:projects response)
                     :more-projects? (:more_projects response)
                      ;;for defining text-message in delete-modal-message
                     :value-used? true}]
     (-> db
         (update :progress disj :check-possible-values-for-deleting)
         (assoc-in [:form-states2 form-id :modal-data] modal-data)))))

;; ===========================================================================
;; Linked List Wizard events
(re-frame/reg-event-fx
  ::show-linked-list-wizard
  (fn [{:keys [db]} [_ field-id field-name]]
    {:db (assoc db :linked-list-wizard {:show? true
                                        :current-step 1
                                        :global-field-id field-id
                                        :global-field-name field-name
                                        :linked-lists []
                                        :loading? true
                                        :selected-field-ids #{}
                                        :search-query ""
                                        :new-global-name ""})
     :dispatch [::fetch-linked-lists field-id]}))

(re-frame/reg-event-fx
  ::fetch-linked-lists
  (fn [{:keys [db]} [_ field-id]]
    (let [account-id (get-in db [:current-account :id])]
      {:http-xhrio
       {:method :get
        :uri (broutes/get-url "global_fields#linked_lists" {:account_id account-id :global_field_id field-id})
        :format (ajax/json-request-format)
        :response-format (ajax/json-response-format {:keywords? true})
        :on-success [::fetch-linked-lists-success]
        :on-failure [::fetch-linked-lists-failure]}})))

(re-frame/reg-event-db
  ::fetch-linked-lists-success
  (fn [db [_ response]]
    (-> db
        (assoc-in [:linked-list-wizard :linked-lists] (:linked_lists response []))
        (assoc-in [:linked-list-wizard :loading?] false))))

(re-frame/reg-event-fx
  ::fetch-linked-lists-failure
  (fn [{:keys [db]} [_ _]]
    {:db (-> db
             (assoc-in [:linked-list-wizard :linked-lists] [])
             (assoc-in [:linked-list-wizard :loading?] false))
     :dispatch [::notifications/error2 (get-translation "global-fields.linked-list.error-fetch" "Failed to fetch linked list fields")]}))

(re-frame/reg-event-db
  ::cancel-linked-list-wizard
  (fn [db _]
    (dissoc db :linked-list-wizard)))

(re-frame/reg-event-db
  ::linked-list-toggle-field
  (fn [db [_ field-id project-id]]
    (let [selected (get-in db [:linked-list-wizard :selected-field-ids] #{})
          already-selected? (contains? selected field-id)]
      (if already-selected?
        (update-in db [:linked-list-wizard :selected-field-ids] disj field-id)
        ;; One-per-project: remove any other field from same project, then add new one
        (let [linked-lists (get-in db [:linked-list-wizard :linked-lists] [])
              same-project-ids (->> linked-lists
                                    (filter #(= (:project_id %) project-id))
                                    (map :id))
              cleared (reduce disj selected same-project-ids)]
          (assoc-in db [:linked-list-wizard :selected-field-ids] (conj cleared field-id)))))))

(re-frame/reg-event-db
  ::linked-list-update-search
  (fn [db [_ query]]
    (assoc-in db [:linked-list-wizard :search-query] query)))

(re-frame/reg-event-db
  ::linked-list-update-name
  (fn [db [_ name]]
    (assoc-in db [:linked-list-wizard :new-global-name] name)))

(re-frame/reg-event-db
  ::linked-list-next-step
  (fn [db _]
    (update-in db [:linked-list-wizard :current-step] inc)))

(re-frame/reg-event-db
  ::linked-list-previous-step
  (fn [db _]
    (update-in db [:linked-list-wizard :current-step] dec)))

(re-frame/reg-event-fx
  ::linked-list-select-global-field
  (fn [{:keys [db]} [_ field-id field-name]]
    {:db (-> db
             (assoc-in [:linked-list-wizard :global-field-id] field-id)
             (assoc-in [:linked-list-wizard :global-field-name] field-name)
             (assoc-in [:linked-list-wizard :selected-field-ids] #{})
             (assoc-in [:linked-list-wizard :search-query] "")
             (assoc-in [:linked-list-wizard :linked-lists] [])
             (assoc-in [:linked-list-wizard :loading?] true))
     :dispatch [::fetch-linked-lists field-id]}))

(re-frame/reg-event-fx
  ::linked-list-import
  (fn [{:keys [db]} _]
    (let [wizard (get db :linked-list-wizard)
          account-id (get-in db [:current-account :id])
          global-field-id (:global-field-id wizard)
          selected-ids (vec (:selected-field-ids wizard))
          new-name (:new-global-name wizard)]
      {:db (update db :progress conj ::linked-list-import-progress)
       :http-xhrio
       {:method :post
        :uri (broutes/get-url "global_fields#import_linked_lists"
                              {:account_id account-id :global_field_id global-field-id})
        :params {:field_ids selected-ids
                 :name new-name}
        :format (ajax/json-request-format)
        :response-format (ajax/json-response-format {:keywords? true})
        :on-success [::linked-list-import-success]
        :on-failure [::linked-list-import-failure]}})))

(re-frame/reg-event-fx
  ::linked-list-import-success
  (fn [{:keys [db]} [_ response]]
    (let [created-field (:field response)]
      {:db (-> db
               (update :progress disj ::linked-list-import-progress)
               (update :global-fields conj created-field)
               (dissoc :linked-list-wizard))
       :dispatch [::notifications/success2
                  (get-translation "global-fields.linked-list.import-success" "Import started successfully. You will be notified when it is complete.")]})))

(re-frame/reg-event-fx
  ::linked-list-import-failure
  (fn [{:keys [db]} [_ response]]
    (let [error-message (get-in response [:response :message] (get-translation "global-fields.linked-list.error-import" "Failed to create global linked list field"))]
      {:db (update db :progress disj ::linked-list-import-progress)
       :dispatch [::notifications/error2 error-message]})))

(re-frame/reg-event-fx
 ::migration-add-value-to-global-field
 (fn [{:keys [db]} [_ value]]
   (let [selected-global-field (get-in db [:form-states2 migration-wizard-form-id :fields :selected-global-field :value])]
     (if selected-global-field
       {:db (assoc-in db [:form-states2 migration-wizard-form-id :fields :selected-global-field :value]
                      (update selected-global-field :values (fn [values]
                                                              (if (map? values)
                                                                values
                                                                (conj (or values []) value)))))
        :dispatch [::notifications/success2
                   (get-translation "testset.notification.new-list-value-added" "New list value added")
                   {:auto-close 1000}]}
       {:db db}))))

(re-frame/reg-event-fx
  ::fetch-linked-project-names
  (fn [{:keys [db]} [_ field-id]]
    (if (get-in db [:linked-project-names field-id])
      {:db db}
      (let [account-id (get-in db [:current-account :id])]
        {:db (assoc-in db [:linked-project-names field-id :loading?] true)
         :http-xhrio
         {:method :get
          :uri (broutes/get-url "global_fields#linked_project_names" {:account_id account-id :global_field_id field-id})
          :response-format (ajax/json-response-format {:keywords? true})
          :on-success [::fetch-linked-project-names-success field-id]
          :on-failure [::fetch-linked-project-names-failure field-id]}}))))

(re-frame/reg-event-db
  ::fetch-linked-project-names-success
  (fn [db [_ field-id response]]
    (assoc-in db [:linked-project-names field-id] {:loading? false
                                                   :projects (:projects response [])})))

(re-frame/reg-event-db
  ::fetch-linked-project-names-failure
  (fn [db [_ field-id]]
    (assoc-in db [:linked-project-names field-id] {:loading? false
                                                   :projects []})))
